[<AutoOpen>]
module Shared.Types

open System

// First define shared types used in API
type Todo = { Id: Guid; Description: string }

type EnvironmentType =
    | LIVE
    | UAT
    | Dev
    | Other of string

type LoginContext =
    {
        EnvType: EnvironmentType
        EnvName: string
        IGMessage: string option
        SupportMessage: string option
        WindowsAuth: string option
        SupportsOIDC: bool
        /// Supplied if the application is run under a sub-path, e.g. foobar/linksmanager instead of the root
        VirtualPath: string option
    }

type ClientSideUser =
    {
        Username: string
        EmailAddress: string
        EmailHash: string
        Forename: string
        Surname: string
        CreationDate: DateTime
        LastLogin: DateTime option
        Telephone: string option
        AccreditationId: string option
        Id: int
        Token: string
        Admin: bool
        UserType: string
        IsIlly: bool
    }

type LicenseCounts =
    {
        Total: int
        Used: int
    }

type ExternalLinkId = ExternalLinkId of int 
module ExternalLinkId =
    let unwrap (ExternalLinkId x) = x

type ExternalLink =
    {
        Id: ExternalLinkId
        Name: string
        Description: string
    }

type ApplicationId = ApplicationId of int
module ApplicationId =
    let unwrap (ApplicationId x) = x

type Application =
    {
        AppId: ApplicationId
        Name: string
        AuthUrl: string
        LandingUrl: string
        Image: string
        UserLevel: string
    }

type TokenApplication =
    {
        AppId: ApplicationId
        Name: string
        Image: string
    }

type ApplicationType =
    | Federated of Application
    | TokenBased of TokenApplication

type PasswordResetConfirmation =
    {
        Token: Guid
        Password: string
        ConfirmedPassword: string
    }

type Guide =
    {
        Key: string
        Name: string
    }

type EnvironmentDetails =
    {
        Name: string
        Database: string
        TermsOfUse: string option
        SupportDetails: string option
    }

type ChannelTitle = string
type ArticlePermalink = string
type SeenItem = bool

type NewsSource =
    | Internal
    | External of (ChannelTitle * ArticlePermalink)

type NewsItem =
    {
        Id: uint32
        Headline: string
        Date: DateTime
        Content: string
        Author: string
        Source: NewsSource
    }

module NewsItem =
    let toIdentifier item = item.Id

type NewsItemWithSeenStatus = SeenItem * NewsItem

type NewsArticles =
    | LMArticlesOnly of NewsItem list
    | LMArticlesOnlyExternalUnavailable of NewsItem list
    | CombinedArticles of NewsItem list

module NewsArticles =
    let toArticles =
        function
        | LMArticlesOnly articles
        | LMArticlesOnlyExternalUnavailable articles
        | CombinedArticles articles -> articles

type DatedFailure =
    {
        Date: DateTime
        IPAddress: string
    }

type MFAResult =
    | Success of ClientSideUser
    | Incorrect
    | Throttle
    | Expired

type IPAddress = IPAddress of string
type UserAgent = UserAgent of string

module IPAddress =
    let unwrap = function IPAddress x -> x

module UserAgent =
    let unwrap = function UserAgent x -> x

type SecuredRoute =
    | Admin
    | IllyAdmin
    | Standard

module SecuredRoute =
    let [<Literal>] ADMIN_BRANCH = "admin"
    let [<Literal>] STANDARD_BRANCH = "standard"
    let [<Literal>] ILLY_ADMIN_BRANCH = "illy-admin"
    
    
    let toUrl x =
        match x with 
        | Admin -> ADMIN_BRANCH
        | IllyAdmin -> ILLY_ADMIN_BRANCH
        | Standard -> STANDARD_BRANCH

type SubRoute =
    | Unsecured
    | Secured of SecuredRoute
    | MFA
    | OpenId

module SubRoute =
    let [<Literal>] UNSECURED_BRANCH = "unsecured"
    let [<Literal>] SECURED_BRANCH = "secured"
    let [<Literal>] MFA_BRANCH = "mfa"
    let [<Literal>] OPENID_BRANCH = "openid"

    let toUrl x =
        match x with 
        | Unsecured -> UNSECURED_BRANCH
        | Secured sr -> sprintf "%s/%s" SECURED_BRANCH (SecuredRoute.toUrl sr)
        | MFA -> MFA_BRANCH
        | OpenId -> OPENID_BRANCH

// Routing module
module Route =

    let LINKS_MANAGER_PATH =
        #if DEBUG
        sprintf ""
        #else
        sprintf "/LinksManager"
        #endif

    let [<Literal>] ROOT = "api"

    let clientSideSubRoute subRoute =
        sprintf "%s/%s/%s" LINKS_MANAGER_PATH ROOT (SubRoute.toUrl subRoute)

    let clientSideBuilder subRoute typeName methodName =
        sprintf "%s/%s/%s" (clientSideSubRoute subRoute) typeName methodName

    ///The server-side builder is partial.
    ///Each individual sub-route should parse the url manually in order to apply its security checks
    let serverSideBuilder typeName methodName =
        sprintf "/%s/%s" typeName methodName